import { Box, Tooltip, useTheme } from '@mui/joy';
import { useAnimate } from 'framer-motion';
import { useEffect, useMemo } from 'react';

export interface StateProps {
	label?: string;
	available?: boolean;
	active?: boolean;
	activeColor?: 'default' | 'donors' | 'opportunities' | 'candidates';
	onClick?: () => void;
	tooltipLabel?: string;
	animationDelay?: number;
	largeCountry?: boolean;
}

export const State = ({
	label,
	available,
	active,
	activeColor,
	onClick,
	tooltipLabel,
	animationDelay = 0,
	largeCountry = false
}: StateProps) => {
	const theme = useTheme();

	const backgroundColor = useMemo(() => {
		if (!available) {
			return theme.colorSchemes.light.palette.chartElementsBackground.gridLines;
		}
		if (activeColor === 'donors') {
			if (active) {
				return theme.colorSchemes.light.palette.chaptersDonors.solidBoldBg;
			}
			return theme.colorSchemes.light.palette.chaptersDonors.solidBg;
		}
		if (activeColor === 'opportunities') {
			if (active) {
				return theme.colorSchemes.light.palette.chaptersOpportunities.solidBg;
			}
			return theme.colorSchemes.light.palette.chartElementsBackground.subTitle;
		}
		if (activeColor === 'candidates') {
			if (active) {
				return '#00A1A8';
			}
			return theme.colorSchemes.light.palette.chaptersCandidates.solidBg;
		}
		return theme.colorSchemes.light.palette.chartElementsBackground.title;
	}, [theme, active, available, activeColor]);

	const labelColor = useMemo(() => {
		if (!available) {
			return theme.colorSchemes.light.palette.chartElementsBackground
				.yAxisLabels;
		}

		if (activeColor === 'donors') {
			if (active) {
				return theme.colorSchemes.light.palette.chaptersDonors.solidColor;
			}
			return theme.colorSchemes.light.palette.chaptersDonors.solidColor;
		}
		if (activeColor === 'opportunities') {
			if (active) {
				return theme.colorSchemes.light.palette.chaptersOpportunities
					.solidColor;
			}
			return '#FAFAFA';
		}
		if (activeColor === 'candidates') {
			return theme.colorSchemes.light.palette.chaptersCandidates.solidColor;
		}
		return theme.colorSchemes.light.palette.chartElementsBackground.title;
	}, [theme, active, available, activeColor]);

	const [scope, animate] = useAnimate();
	useEffect(() => {
		void animate(
			scope.current,
			{
				backgroundColor: backgroundColor,
				borderColor: active ? '#333333' : backgroundColor
			},
			{ duration: 1, delay: animationDelay }
		);
	}, [animate, scope, active, backgroundColor, animationDelay]);

	return (
		<Tooltip title={tooltipLabel}>
			<Box
				ref={scope}
				sx={{
					width: {
						xs: label ? '22.62px' : largeCountry ? '17.14px' : '12.61px',
						sm: label ? '25.62px' : largeCountry ? '17.14px' : '12.61px',
						md: label ? '35.01px' : largeCountry ? '17.14px' : '12.61px'
					},
					height: {
						xs: label ? '22.62px' : largeCountry ? '17.14px' : '12.61px',
						sm: label ? '25.62px' : largeCountry ? '17.14px' : '12.61px',
						md: label ? '35.01px' : largeCountry ? '17.14px' : '12.61px'
					},
					backgroundColor:
						theme.colorSchemes.light.palette.chartElementsBackground.gridLines,
					borderColor:
						theme.colorSchemes.light.palette.chartElementsBackground.gridLines,
					borderWidth: 1,
					borderStyle: 'solid',
					letterSpacing: '2px',
					fontFamily: 'Roboto, sans-serif',
					fontSize: { xs: '0.625rem', md: '0.875rem' },
					color: labelColor,
					fontWeight: active ? 'bold' : 'normal',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					transition: 'transform 0.2s ease-in-out', // Add a transition for a smooth hover effect
					':hover': {
						transform: available ? 'scale(1.1)' : 'none' // Adjust the scale factor as needed
					},
					cursor: available ? 'pointer' : 'default' // Change the cursor to pointer if available
				}}
				onClick={available && onClick ? () => onClick() : undefined}
			>
				{label}
			</Box>
		</Tooltip>
	);
};
